<template>
  <div class="inbox-page overflow-x-hidden h-full bg-gray-50">
    <!-- Header -->
    <div class="bg-primary text-white px-6 py-4 sticky top-0 z-20">
      <div class="mobile-status-padding"></div>
      <div class="flex justify-between items-center">
        <div class="flex items-center gap-3">
          <button
            v-if="showBackButton"
            @click="goBack"
            class="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 class="text-xl font-bold">Boîte de réception</h1>
        </div>

        <div class="flex items-center gap-2">
          <!-- Mark All Read Button (if has unread) -->
          <button
            v-if="hasUnread && !isMarkingRead"
            @click="markAllAsRead"
            class="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            Tout marquer comme lu
          </button>

          <!-- Loading State for Mark All Read -->
          <div
            v-if="isMarkingRead"
            class="px-3 py-1.5 bg-white/20 rounded-lg flex items-center gap-2"
          >
            <div
              class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"
            ></div>
            <span class="text-sm">Marquage...</span>
          </div>
        </div>
      </div>

      <!-- Unread Count Badge -->
      <div v-if="unreadCount > 0" class="mt-2 flex items-center gap-2">
        <span class="text-sm opacity-90"
          >{{ unreadCount }} notification(s) non lue(s)</span
        >
      </div>
    </div>
    <!-- Filters -->
    <NotificationFilters
      :unread-count="unreadCount"
      :notifications="notifications"
      @filter-change="handleFilterChange"
    />

    <!-- Notification List -->
    <NotificationList
      :filter-type="currentFilter"
      :filter-read-status="readStatusFilter"
      @notification-click="handleNotificationClick"
      @notification-delete="handleNotificationDelete"
    />

    <!-- Notification Details Modal -->
    <DialogComponent
      ref="notificationModal"
      dialogId="notification-details-modal"
      type="modal"
      noInnerPadding
    >
      <div v-if="selectedNotification" class="p-6">
        <div class="flex items-start gap-3.5 mb-4">
          <div
            :class="[
              'w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0',
              getIconBgColor(selectedNotification.type)
            ]"
          >
            <ImgComponent
              fallback="/images/all/bell.svg"
              :src="selectedNotification.icon"
              :alt="selectedNotification.type"
              class="w-6 h-6"
            />
          </div>
          <div class="flex-1 min-w-0 pr-6">
            <div class="flex items-center gap-2 flex-wrap mb-1">
              <span
                :class="[
                  'px-2 py-0.5 rounded-full text-xs font-semibold',
                  getNotificationBadgeClass(selectedNotification.type)
                ]"
              >
                {{ getNotificationTypeLabel(selectedNotification.type) }}
              </span>
              <span
                v-if="selectedNotification.priority === 'high'"
                class="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"
              >
                Urgent
              </span>
              <span class="text-xs text-gray-400">
                {{ formatNotificationDate(selectedNotification.createdAt) }}
              </span>
            </div>
            <h3 class="text-lg font-bold text-gray-900 leading-snug">
              {{ selectedNotification.title }}
            </h3>
          </div>
        </div>

        <div class="bg-gray-50 rounded-2xl p-4 mb-6 border border-gray-100">
          <p class="text-gray-700 text-sm leading-relaxed whitespace-pre-line wrap-break-word">
            {{ selectedNotification.message }}
          </p>
        </div>

        <div class="flex items-center gap-3 justify-end">
          <button
            type="button"
            @click="closeNotificationModal"
            class="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Fermer
          </button>
          <button
            v-if="selectedNotification.actionUrl"
            type="button"
            @click="handleNotificationAction"
            class="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors flex items-center gap-2 shadow-sm shadow-primary/30"
          >
            <span>{{ selectedNotification.action || 'Consulter' }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </DialogComponent>

    <!-- Empty State (when no notifications at all) -->
    <div
      v-if="!isLoading && notifications.length === 0"
      class="flex flex-col items-center justify-center py-20 px-4"
    >
      <ImgComponent
        src="/images/empty/notifications.svg"
        alt="Aucune notification"
        class="w-40 h-40 mb-6 opacity-50"
      />
      <h3 class="text-xl font-bold text-gray-700 mb-2">
        Votre boîte de réception est vide
      </h3>
      <p class="text-gray-500 text-center max-w-sm">
        Vous n'avez pas encore de notifications. Nous vous informerons ici des
        mises à jour importantes.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import NotificationFilters from '../components/NotificationFilters.vue';
import NotificationList from '../components/NotificationList.vue';
import { formatNotificationDate } from '../serializer';

const router = useRouter();

// State
const showBackButton = ref(router.currentRoute.value.meta.showBackButton || false);
const currentFilter = ref('all');
const readStatusFilter = ref('all');
const isMarkingRead = ref(false);
const notificationModal = ref(null);
const selectedNotification = ref(null);

// Helpers for modal styling
const getIconBgColor = (type) => {
  const colorMap = {
    order: 'bg-blue-100',
    promotion: 'bg-green-100',
    system: 'bg-gray-100',
    payment: 'bg-yellow-100',
    delivery: 'bg-purple-100'
  };
  return colorMap[type] || 'bg-gray-100';
};

const getNotificationTypeLabel = (type) => {
  const labelMap = {
    order: 'Commande',
    promotion: 'Promotion',
    system: 'Système',
    payment: 'Paiement',
    delivery: 'Livraison'
  };
  return labelMap[type] || 'Information';
};

const getNotificationBadgeClass = (type) => {
  const classMap = {
    order: 'bg-blue-100 text-blue-800',
    promotion: 'bg-green-100 text-green-800',
    system: 'bg-gray-100 text-gray-800',
    payment: 'bg-yellow-100 text-yellow-800',
    delivery: 'bg-purple-100 text-purple-800'
  };
  return classMap[type] || 'bg-gray-100 text-gray-800';
};

// Store access
const getStore = () => {
  return window.$stores?.inbox;
};

// Computed properties from store
const notifications = computed(() => {
  const store = getStore();
  return store ? store.notifications : [];
});

const unreadCount = computed(() => {
  const store = getStore();
  return store ? store.unreadCount : 0;
});

const hasUnread = computed(() => {
  return unreadCount.value > 0;
});

const isLoading = computed(() => {
  const store = getStore();
  return store ? store.isLoading : false;
});

// Methods
const goBack = () => {
  router.back();
};

const markAllAsRead = () => {
  if (isMarkingRead.value) return;

  bus.emit(constants.EVENTS.INBOX.NOTIFICATION_MARK_ALL_READ);
};

const handleFilterChange = (filters) => {
  currentFilter.value = filters.type;
  readStatusFilter.value = filters.readStatus;
};

const handleNotificationClick = (notification) => {
  selectedNotification.value = notification;

  // Mark as read if not already read
  if (!notification.read) {
    bus.emit(constants.EVENTS.INBOX.NOTIFICATION_MARK_READ, notification.id);
  }

  // Open modal
  notificationModal.value?.open ? notificationModal.value.open() : notificationModal.value?.show();
};

const closeNotificationModal = () => {
  notificationModal.value?.close ? notificationModal.value.close() : notificationModal.value?.hide();
};

const handleNotificationAction = () => {
  if (selectedNotification.value?.actionUrl) {
    const url = selectedNotification.value.actionUrl;
    closeNotificationModal();
    bus.emit('navigation:requested', { url });
  }
};

const handleNotificationDelete = (notificationId) => {
  console.log('Notification deleted:', notificationId);
};

// Setup event listeners for store state updates
const setupEventListeners = () => {
  // Listen for marking read state changes
  bus.on(constants.EVENTS.INBOX.NOTIFICATION_MARKING_ALL_READ, () => {
    isMarkingRead.value = true;
  });

  bus.on(constants.EVENTS.INBOX.NOTIFICATION_MARKED_ALL_READ, () => {
    isMarkingRead.value = false;
  });

  bus.on(constants.EVENTS.INBOX.NOTIFICATION_MARK_ALL_READ_ERROR, () => {
    isMarkingRead.value = false;
  });
};

const cleanupEventListeners = () => {
  bus.off(constants.EVENTS.INBOX.NOTIFICATION_MARKING_ALL_READ);
  bus.off(constants.EVENTS.INBOX.NOTIFICATION_MARKED_ALL_READ);
  bus.off(constants.EVENTS.INBOX.NOTIFICATION_MARK_ALL_READ_ERROR);
};

// Lifecycle
onMounted(() => {
  // Load notifications on mount
  bus.emit(constants.EVENTS.INBOX.NOTIFICATIONS_LOAD);

  // Setup event listeners
  setupEventListeners();
});

onUnmounted(() => {
  // Cleanup event listeners
  cleanupEventListeners();
});
</script>

<style scoped>
.inbox-page {
  min-height: 100vh;
}
</style>
